import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { usePublicPageScroll } from '../../../hooks/usePublicPageScroll';
import { Table, THead, TBody, TR, TH, TD } from '../../shared/DataTable';
import Alert from '../../shared/Alert';
import CloseButton from '../../shared/CloseButton';
import Button from '../../shared/Button';
import LoadingSpinner from '../../shared/LoadingSpinner';
import { FIELD_INPUT_CLASS, FIELD_LABEL_CLASS } from '../../shared/FormInput';
import { TEXT } from '../../shared/Typography';

/* --------------------------------- Types -------------------------------- */
interface DocketPhoto {
  fileId: string;
  filename: string;
  base64Data: string;
  mimeType: string;
}

interface DocketInfo {
  ctrNumber: string;
  reference: string;
  dispatchDate: string;
  fromLocation: string;
  toLocation: string;
  checklist: Array<{ item: string; answer: string }>;
  packedBy: { name: string };
  items: Array<{ tagNumber: string; serialNo: string }>;
  outboundPhotos?: DocketPhoto[];
}

/* ----------------------------- Signature Canvas ------------------------- */
function SignatureCanvas({
  onSignature,
}: {
  onSignature: (dataUrl: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getPos = (e: any) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const startDraw = (e: any) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    setDrawing(true);
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: any) => {
    e.preventDefault();
    if (!drawing) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = () => {
    setDrawing(false);
    if (hasDrawn && canvasRef.current) {
      onSignature(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onSignature('');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        className="border border-gray-300 rounded-lg bg-white cursor-crosshair w-full touch-none"
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <div className="flex justify-end mt-1">
        <Button variant="linkMuted" size="xs" onClick={clear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

/* ----------------------------- Main Page -------------------------------- */
export default function DocketSignPage() {
  usePublicPageScroll();
  const [status, setStatus] = useState<'loading' | 'ready' | 'signing' | 'signed' | 'error'>(
    'loading'
  );
  const [errorMsg, setErrorMsg] = useState('');
  const [docket, setDocket] = useState<DocketInfo | null>(null);
  const [name, setName] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [receivedPhotos, setReceivedPhotos] = useState<File[]>([]);
  const [receivedPreviews, setReceivedPreviews] = useState<string[]>([]);

  // Extract token from URL
  const token = new URLSearchParams(window.location.search).get('token') || '';

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('No signing token provided.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/mech/movements/docket-sign/${token}`);
        if (cancelled) return;
        setDocket(data);
        setStatus('ready');
      } catch (e: any) {
        if (cancelled) return;
        const msg =
          e?.response?.data?.error || 'Unable to load docket. The link may have expired.';
        setErrorMsg(msg);
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      setErrorMsg('Please enter your name.');
      return;
    }
    if (!signatureData) {
      setErrorMsg('Please draw your signature.');
      return;
    }

    setStatus('signing');
    setErrorMsg('');

    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('signatureBase64', signatureData);
      receivedPhotos.forEach((f) => formData.append('receivedPhotos', f));

      await axios.post(`/api/mech/movements/docket-sign/${token}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStatus('signed');
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Failed to submit signature.';
      setErrorMsg(msg);
      setStatus('ready');
    }
  }, [token, name, signatureData]);

  const formatDate = (v: string) => {
    if (!v) return '—';
    try {
      const d = new Date(v);
      return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner message="Loading docket..." />
      </div>
    );
  }

  // Error state
  if (status === 'error' && !docket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <div className="text-red-600 text-4xl mb-4">&#9888;</div>
          <h2 className={`mb-2 ${TEXT.sectionTitle}`}>Unable to Load Docket</h2>
          <p className={TEXT.bodyMuted}>{errorMsg}</p>
        </div>
      </div>
    );
  }

  // Signed success state
  if (status === 'signed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-8 max-w-md text-center">
          <div className="text-green-600 text-4xl mb-4">&#10004;</div>
          <h2 className={`mb-2 ${TEXT.sectionTitle}`}>Docket Signed Successfully</h2>
          <p className={TEXT.bodyMuted}>
            Thank you. Your signature has been recorded. You may close this page.
          </p>
        </div>
      </div>
    );
  }

  if (!docket) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-blue-900 text-white px-6 py-4">
            <h1 className="text-xl font-bold">PSV Transport Docket</h1>
            <p className="text-blue-200 text-sm mt-1">
              Please review the delivery details and sign below to confirm receipt.
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Details */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className={TEXT.detailLabel}>CTR Number</span>
                <div className="text-gray-900">{docket.ctrNumber || '—'}</div>
              </div>
              <div>
                <span className={TEXT.detailLabel}>PO Number</span>
                <div className="text-gray-900">{docket.reference || '—'}</div>
              </div>
              <div>
                <span className={TEXT.detailLabel}>Dispatch Date</span>
                <div className="text-gray-900">{formatDate(docket.dispatchDate)}</div>
              </div>
              <div>
                <span className={TEXT.detailLabel}>Packed By</span>
                <div className="text-gray-900">{docket.packedBy?.name || '—'}</div>
              </div>
              <div>
                <span className={TEXT.detailLabel}>Pickup Location</span>
                <div className="text-gray-900">{docket.fromLocation || '—'}</div>
              </div>
              <div>
                <span className={TEXT.detailLabel}>Drop Off Location</span>
                <div className="text-gray-900">{docket.toLocation || '—'}</div>
              </div>
            </div>

            {/* Checklist */}
            {docket.checklist?.length > 0 && (
              <div>
                <h3 className={`mb-2 ${TEXT.cardTitle}`}>Safety Checklist</h3>
                <div className="space-y-1">
                  {docket.checklist.map((cl, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-2 text-sm p-2 rounded-md ${
                        cl.answer === 'yes' ? 'bg-green-50' : cl.answer === 'no' ? 'bg-red-50' : 'bg-gray-50'
                      }`}
                    >
                      <span className={`font-bold ${cl.answer === 'yes' ? 'text-green-600' : 'text-red-600'}`}>
                        {cl.answer === 'yes' ? 'Y' : 'N'}
                      </span>
                      <span className="text-gray-700">{cl.item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Items */}
            {docket.items?.length > 0 && (
              <div>
                <h3 className={`mb-2 ${TEXT.cardTitle}`}>Items Delivered</h3>
                <Table
                  density="compact"
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <THead>
                    <TR>
                      <TH>Tag Number</TH>
                      <TH>Serial Number</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {docket.items.map((it, i) => (
                      <TR key={i}>
                        <TD>{it.tagNumber || '—'}</TD>
                        <TD>{it.serialNo || '—'}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            )}

            {/* Outbound photos */}
            {docket.outboundPhotos && docket.outboundPhotos.length > 0 && (
              <div>
                <h3 className={`mb-2 ${TEXT.cardTitle}`}>Outbound Photos</h3>
                <div className="flex flex-wrap gap-2">
                  {docket.outboundPhotos.map((p, i) => (
                    <img
                      key={i}
                      src={p.base64Data}
                      alt={`Outbound ${i + 1}`}
                      className="w-28 h-28 object-cover rounded-lg border border-gray-200"
                    />
                  ))}
                </div>
              </div>
            )}

            <hr className="border-gray-200" />

            {/* Received photos upload */}
            <div>
              <h3 className={`mb-2 ${TEXT.cardTitle}`}>Received Photos</h3>
              <p className={`mb-2 ${TEXT.caption}`}>
                Take or upload photos of items as received.
              </p>
              <label className="block w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length) {
                      setReceivedPhotos((prev) => [...prev, ...files]);
                      setReceivedPreviews((prev) => [
                        ...prev,
                        ...files.map((f) => URL.createObjectURL(f)),
                      ]);
                    }
                  }}
                />
                <span className={TEXT.helper}>Tap to take photo or select files</span>
              </label>
              {receivedPreviews.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {receivedPreviews.map((url, i) => (
                    <div key={i} className="relative w-20 h-20">
                      <img src={url} alt={`Received ${i + 1}`} className="w-full h-full object-cover rounded-lg border border-gray-200" />
                      <CloseButton onClick={() => {
                          URL.revokeObjectURL(url);
                          setReceivedPhotos((prev) => prev.filter((_, j) => j !== i));
                          setReceivedPreviews((prev) => prev.filter((_, j) => j !== i));
                        }} label="Remove" className="absolute -top-1 -right-1" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Signature section */}
            <div>
              <h3 className={`mb-3 ${TEXT.cardTitle}`}>Confirm Receipt</h3>

              {errorMsg && (
                <Alert variant="error" message={errorMsg} className="mb-3" />
              )}

              <div className="space-y-3">
                <div>
                  <label htmlFor="docket-sign-name" className={FIELD_LABEL_CLASS}>Your Name</label>
                  <input
                    id="docket-sign-name"
                    className={FIELD_INPUT_CLASS}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className={FIELD_LABEL_CLASS}>Signature</label>
                  <SignatureCanvas onSignature={setSignatureData} />
                </div>

                <Button variant="primary" size="lg" onClick={handleSubmit} disabled={status === 'signing'} fullWidth>
                  {status === 'signing' ? 'Submitting...' : 'Sign & Confirm Receipt'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <p className={`text-center mt-4 ${TEXT.caption}`}>
          GR Production Services &bull; 00MNNFOR003
        </p>
      </div>
    </div>
  );
}
